import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, euro, errMsg } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { SellerBadge } from "@/components/SellerBadge";
import { FileDown, Loader2 } from "lucide-react";
import { DocumentViewer } from "@/components/DocumentViewer";
import { DOC_LABELS, REQUIRED_DOCS } from "@/components/DocumentUploads";

const DOC_SHORT = { id_card: "Identità", health_card: "Tessera san.", chamber: "Visura", id_card_front: "Identità F",
  id_card_back: "Identità R", health_card_front: "Tessera F", health_card_back: "Tessera R" };

const UserStatus = ({ u }) => {
  if (u.role === "admin") return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-900 text-white">Admin</span>;
  const map = { pending: ["In attesa", "bg-amber-50 text-amber-700"], approved: ["Approvato", "bg-emerald-50 text-emerald-700"], rejected: ["Rifiutato", "bg-rose-50 text-rose-700"] };
  const [label, cls] = map[u.status] || ["Registrazione incompleta", "bg-slate-100 text-slate-600"];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`} data-testid={`admin-user-status-${u.user_id}`}>{label}</span>;
};

const Stat = ({ label, value, id }) => (
  <div className="p-5 bg-white border border-slate-200 rounded-2xl">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono text-2xl mt-1" data-testid={id}>{value}</p>
  </div>
);

export default function Admin() {
  const api = useApi();
  const { me } = useUser();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ roles: [], users: [] });
  const [listings, setListings] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadCatalog = async () => {
    setPdfBusy(true);
    try {
      const blob = await api("get", "/admin/catalog.pdf", null, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `catalogo-buynow-${new Date().toISOString().slice(0, 10)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errMsg(e)); } finally { setPdfBusy(false); }
  };

  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const [s, u, l] = await Promise.all([api("get", "/admin/stats"), api("get", "/users"), api("get", "/admin/listings")]);
      setStats(s); setUsers(u); setListings(l); setError("");
    } catch (e) { setError(errMsg(e)); }
  }, [api]);
  useEffect(() => { if (me?.role === "admin") load(); }, [me, load]);

  if (me && me.role !== "admin") return <div className="py-24 text-center" data-testid="admin-forbidden">Accesso riservato agli amministratori.</div>;
  if (error && !stats) return <div className="py-24 text-center text-rose-600" data-testid="admin-error">{error}</div>;
  if (!stats) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-500">Caricamento...</div>;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const reject = (u) => {
    const note = window.prompt("Motivo (verrà mostrato all'utente):", "Documenti non validi o illeggibili");
    if (note !== null) act(() => api("patch", `/users/${u.user_id}/status`, { status: "rejected", note }), "Registrazione rifiutata");
  };
  const ORDER = { pending: 0, rejected: 2, approved: 3 };
  const sortedUsers = [...users.users].sort((a, b) => (ORDER[a.status] ?? 1) - (ORDER[b.status] ?? 1));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Pannello Amministrazione</h1>
        <div className="flex gap-2">
          <Button variant="outline" disabled={pdfBusy} onClick={downloadCatalog} data-testid="admin-download-catalog-button" className="rounded-full">
            {pdfBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileDown size={16} className="mr-2" />}Scarica catalogo PDF
          </Button>
          <Button asChild data-testid="admin-publish-vehicle-button" className="rounded-full bg-blue-600 hover:bg-blue-700"><Link to="/vendi">Pubblica veicolo</Link></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mt-8">
        <Stat label="Da approvare" value={stats.pending_users} id="admin-stat-pending-users" />
        <Stat label="Utenti" value={stats.users} id="admin-stat-users" />
        <Stat label="Annunci attivi" value={stats.listings_active} id="admin-stat-active" />
        <Stat label="Venduti" value={stats.listings_sold} id="admin-stat-sold" />
        <Stat label="Offerte attive" value={stats.offers_pending} id="admin-stat-offers" />
        <Stat label="Volume" value={euro(stats.volume)} id="admin-stat-volume" />
      </div>
      <Tabs defaultValue={stats.pending_users ? "utenti" : "annunci"} className="mt-10">
        <TabsList>
          <TabsTrigger value="annunci" data-testid="admin-tab-listings">Moderazione annunci</TabsTrigger>
          <TabsTrigger value="utenti" data-testid="admin-tab-users">Utenti e approvazioni{stats.pending_users ? ` (${stats.pending_users})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="annunci" className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <Table data-testid="admin-listings-table">
            <TableHeader><TableRow><TableHead>Veicolo</TableHead><TableHead>Venditore</TableHead><TableHead>Prezzo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {listings.map((l) => (
                <TableRow key={l.id} data-testid={`admin-listing-row-${l.id}`}>
                  <TableCell><Link to={`/veicolo/${l.id}`} className="font-medium hover:text-blue-600">{l.brand} {l.model}</Link></TableCell>
                  <TableCell><div className="flex items-center gap-2">{l.seller_name}<SellerBadge type={l.seller_type} /></div></TableCell>
                  <TableCell className="font-mono">{euro(l.price)}</TableCell>
                  <TableCell>{{ active: "Attivo", hidden: "Sospeso", sold: "Venduto" }[l.status]}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {l.status === "active" && <Button size="sm" variant="outline" data-testid={`admin-moderate-listing-hide-${l.id}`} onClick={() => act(() => api("patch", `/admin/listings/${l.id}`, { status: "hidden" }), "Annuncio sospeso")}>Sospendi</Button>}
                    {l.status === "hidden" && <Button size="sm" variant="outline" data-testid={`admin-moderate-listing-approve-${l.id}`} onClick={() => act(() => api("patch", `/admin/listings/${l.id}`, { status: "active" }), "Annuncio riattivato")}>Riattiva</Button>}
                    <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`admin-delete-listing-${l.id}`} onClick={() => window.confirm("Eliminare definitivamente?") && act(() => api("delete", `/listings/${l.id}`), "Annuncio eliminato")}>Elimina</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="utenti" className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <Table data-testid="admin-users-table">
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Tipo</TableHead><TableHead>Documenti</TableHead><TableHead>Stato</TableHead><TableHead>Ruolo</TableHead></TableRow></TableHeader>
            <TableBody>
              {sortedUsers.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                  <TableCell className="font-medium">{u.company_name || u.display_name}<div className="text-xs text-slate-500">{[u.city, u.phone].filter(Boolean).join(" · ")}</div></TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.account_type ? <SellerBadge type={u.account_type} /> : "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {u.role !== "admin" && [...new Set([...(REQUIRED_DOCS[u.account_type] || []), ...Object.keys(u.documents || {})])].map((k) => u.documents?.[k]?.path === "none" ? (
                        <span key={k} className="text-xs text-slate-500">{DOC_SHORT[k]} (esempio)</span>
                      ) : u.documents?.[k] ? (
                        <Button key={k} size="sm" variant="outline" className="h-7 text-xs" data-testid={`admin-view-doc-${k}-${u.user_id}`}
                          onClick={() => setViewing({ userId: u.user_id, kind: k, title: `${DOC_LABELS[k]} · ${u.company_name || u.display_name}` })}>{DOC_SHORT[k]}</Button>
                      ) : <span key={k} className="text-xs text-rose-600">{DOC_SHORT[k]} mancante</span>)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <UserStatus u={u} />
                      {u.role !== "admin" && u.status === "pending" && <>
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-approve-user-${u.user_id}`} onClick={() => act(() => api("patch", `/users/${u.user_id}/status`, { status: "approved" }), "Utente approvato")}>Approva</Button>
                        <Button size="sm" variant="outline" className="h-7" data-testid={`admin-reject-user-${u.user_id}`} onClick={() => reject(u)}>Rifiuta</Button>
                      </>}
                      {u.role !== "admin" && u.status === "approved" && (
                        <Button size="sm" variant="ghost" className="h-7 text-rose-600" data-testid={`admin-revoke-user-${u.user_id}`} onClick={() => reject(u)}>Revoca</Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(role) => act(() => api("patch", `/users/${u.user_id}/role`, { role }), "Ruolo aggiornato")}>
                      <SelectTrigger className="w-32" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{users.roles.map((r) => <SelectItem key={r} value={r}>{r === "admin" ? "Admin" : "Utente"}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
      {viewing && <DocumentViewer {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
